/**
 * ⭐⭐⭐ **Cetak Z-report tingkat TOKO** — berita acara tutup hari (Imron, 2026-08-25).
 *
 * ### ⛔ Kenapa tombolnya berdiri TEPAT di sebelah tombol settlement
 * Lembar ini terbit dari `business_day`, yang lahir saat settlement — jadi satu-satunya momen ia
 * bisa dicetak adalah SESUDAH tombol di sebelahnya ditekan. Menaruhnya di menu lain berarti bukan
 * "dicetak nanti" melainkan "tak pernah dicetak", persis nasib Z per sesi sebelum ia dipindah ke
 * layar yang sama.
 *
 * ⚠️ Byte tetap dirender SERVER (K27). Yang menyeberang ke agen cuma byte + alamat.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { printViaAgent } from '../api/agent';
import { api } from '../api/client';

export interface StoreReportButtonProps {
  readonly outletCode: string;
  readonly businessDate: string;
}

type Status =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'ok' }
  /** ⚠️ Dibedakan dari galat: rekapnya ADA, yang belum terjadi cuma kertasnya. */
  | { kind: 'recorded'; message: string }
  | { kind: 'error'; message: string };

export function StoreReportButton({ outletCode, businessDate }: StoreReportButtonProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  const cetak = async (): Promise<void> => {
    setStatus({ kind: 'busy' });
    let r;
    try {
      r = await api.storeReport(outletCode, businessDate);
    } catch (e: unknown) {
      /*
       * ⚠️ Pesan servernya ditampilkan APA ADANYA. `DAY-404-NOT_CLOSED` sudah menjelaskan bahwa
       * lembar ini terbit dari settlement — menggantinya dengan "gagal" menghapus satu-satunya
       * petunjuk yang berguna.
       */
      setStatus({ kind: 'error', message: (e as Error).message });
      return;
    }
    const job = r.job;
    if (job === undefined || job === null) {
      setStatus({ kind: 'recorded', message: t('session.report.noDevice') });
      return;
    }
    const kirim = await printViaAgent({ bytesBase64: job.bytesBase64, address: job.address });
    setStatus(kirim.ok
      ? { kind: 'ok' }
      : { kind: 'recorded', message: `${kirim.code ?? ''} ${kirim.message ?? ''}`.trim() });
  };

  return (
    <div className="print-box" data-testid="report-box-store">
      <button
        type="button"
        className="action-secondary"
        data-testid="print-report-store"
        disabled={status.kind === 'busy'}
        onClick={() => void cetak()}
      >
        {status.kind === 'busy' ? t('common.loading') : t('session.report.store')}
      </button>
      {status.kind === 'ok' && (
        <p className="ok" data-testid="store-report-ok">{t('session.report.sent')}</p>
      )}
      {status.kind === 'recorded' && (
        <p className="warn" data-testid="store-report-recorded">{status.message}</p>
      )}
      {status.kind === 'error' && (
        <p className="error" data-testid="store-report-error">{status.message}</p>
      )}
    </div>
  );
}
